import { inspectKicadTextSync, type KicadInspectResult } from "./kicanvas-sources";
import { consumeRevisionFilePrefetch, fetchRevisionFileOnce } from "./revision-file-fetch.ts";
import InspectWorker from "./kicad-inspect.worker.ts?worker";

export type KicadInspectVerdict = { ok: true } | { ok: false; reason: string };

type InspectResponse = { id: number; result: KicadInspectVerdict };

type Pending = {
  path: string;
  text?: string;
  resolve: (result: KicadInspectVerdict) => void;
};

let worker: Worker | null = null;
let workerFailed = false;
let nextId = 1;
const pending = new Map<number, Pending>();

function stripText(result: KicadInspectResult): KicadInspectVerdict {
  return result.ok ? { ok: true } : { ok: false, reason: result.reason };
}

function fallbackPending(): void {
  const leftover = [...pending.values()];
  pending.clear();
  for (const item of leftover) {
    if (typeof item.text === "string") {
      item.resolve(stripText(inspectKicadTextSync(item.path, item.text)));
      continue;
    }
    item.resolve({ ok: false, reason: "inspect worker failed" });
  }
}

function getWorker(): Worker | null {
  if (workerFailed) return null;
  if (worker) return worker;
  if (typeof Worker === "undefined") {
    workerFailed = true;
    return null;
  }
  try {
    worker = new InspectWorker({ name: "kicad-inspect" });
    worker.onmessage = (event: MessageEvent<InspectResponse>) => {
      const item = pending.get(event.data.id);
      if (!item) return;
      pending.delete(event.data.id);
      item.resolve(event.data.result);
    };
    worker.onerror = () => {
      workerFailed = true;
      try {
        worker?.terminate();
      } catch {
        /* ignore */
      }
      worker = null;
      fallbackPending();
    };
    worker.onmessageerror = () => {
      workerFailed = true;
      try {
        worker?.terminate();
      } catch {
        /* ignore */
      }
      worker = null;
      fallbackPending();
    };
    return worker;
  } catch {
    workerFailed = true;
    return null;
  }
}

/** Parse / validate KiCad text off the main thread when Worker is available. */
export function inspectKicadTextOffThread(path: string, text: string): Promise<KicadInspectResult> {
  const host = getWorker();
  if (!host) return Promise.resolve(inspectKicadTextSync(path, text));
  const id = nextId++;
  return new Promise((resolve) => {
    pending.set(id, {
      path,
      text,
      resolve: (verdict) => {
        resolve(verdict.ok ? { ok: true, text } : { ok: false, reason: verdict.reason, text });
      },
    });
    try {
      host.postMessage({ id, path, text });
    } catch {
      pending.delete(id);
      resolve(inspectKicadTextSync(path, text));
    }
  });
}

/**
 * Inspect consumes the prefetch GET when one exists for this href.
 * Otherwise it starts the (only) revision-file GET. Worker never fetches.
 */
export async function inspectKicadHrefOffThread(
  path: string,
  href: string,
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<KicadInspectResult> {
  const res = await (consumeRevisionFilePrefetch(href, signal) ??
    fetchRevisionFileOnce(href, fetchImpl, signal));
  if (!res.ok) return { ok: false, reason: `HTTP ${res.status}`, text: "" };
  const text = await res.text();
  return inspectKicadTextOffThread(path, text);
}

export function resetKicadInspectWorker(): void {
  workerFailed = false;
  pending.clear();
  try {
    worker?.terminate();
  } catch {
    /* ignore */
  }
  worker = null;
}
